import { useCallback, useEffect, useMemo, useState } from "react";
import {
  activateAttendanceRegulation,
  createAttendanceRegulation,
  getAttendanceRegulations,
  updateAttendanceRegulation,
} from "../../api/attendanceRegulations";
import { isHrFamilyRole } from "../../utils/hrStaffHelpers";
import {
  DEFAULT_OVERTIME_HOUR_RATES,
  formatOvertimeHourMultiplierAr,
} from "../../utils/overtimeHourRates";
import "bootstrap/dist/css/bootstrap.rtl.min.css";

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

const PENALTY_ACTIONS = [
  { value: "NONE", label: "بدون جزاء" },
  { value: "WARNING", label: "إنذار بدون خصم" },
  { value: "DEDUCTION", label: "خصم أيام" },
];

function inferAction(row) {
  if (
    row?.action === "WARNING" ||
    row?.action === "NONE" ||
    row?.action === "DEDUCTION"
  ) {
    return row.action;
  }
  return row?.days ? "DEDUCTION" : "NONE";
}

function emptyBracket() {
  return { upToMinutes: "", days: "", action: "NONE" };
}

function bracketsToForm(brackets) {
  if (!Array.isArray(brackets) || !brackets.length) {
    return [emptyBracket()];
  }
  return brackets.map((row) => ({
    upToMinutes: row.upToMinutes == null ? "" : String(row.upToMinutes),
    days: row.days == null ? "" : String(row.days),
    action: inferAction(row),
  }));
}

function formToBrackets(rows) {
  return rows.map((row, index) => {
    const last = index === rows.length - 1;
    const upToRaw = String(row.upToMinutes ?? "").trim();
    const daysRaw = String(row.days ?? "").trim();
    const action = row.action || "NONE";
    return {
      upToMinutes: last && !upToRaw ? null : Number(upToRaw),
      action,
      days: action === "DEDUCTION" && daysRaw ? Number(daysRaw) : null,
    };
  });
}

function punchRuleToForm(rule) {
  return {
    action: inferAction(rule),
    days: rule?.days == null ? "" : String(rule.days),
  };
}

function formToPunchRule(value) {
  const action = value?.action || "NONE";
  const raw = String(value?.days ?? "").trim();
  return {
    action,
    days: action === "DEDUCTION" && raw ? Number(raw) : null,
  };
}

function approvalDelayToForm(rule) {
  return {
    action: rule?.action ? inferAction(rule) : "DEDUCTION",
    days: rule?.days == null ? "0.25" : String(rule.days),
    hours: String(rule?.hours ?? 24),
    gmHours: String(rule?.gmHours ?? 72),
  };
}

function formToApprovalDelay(value) {
  const action = value?.action || "DEDUCTION";
  const daysRaw = String(value?.days ?? "").trim();
  return {
    action,
    days: action === "DEDUCTION" && daysRaw ? Number(daysRaw) : null,
    hours: Number(value?.hours) || 24,
    gmHours: Number(value?.gmHours) || 72,
  };
}

function regulationToForm(reg) {
  return {
    nameAr: reg.nameAr || "",
    descriptionAr: reg.descriptionAr || "",
    gracePeriodMinutes: String(reg.gracePeriodMinutes ?? 7),
    overtimePaidHourMultiplier: String(
      reg.overtimePaidHourMultiplier ??
        DEFAULT_OVERTIME_HOUR_RATES.overtimePaidHourMultiplier,
    ),
    overtimeCompensatoryHourMultiplier: String(
      reg.overtimeCompensatoryHourMultiplier ??
        DEFAULT_OVERTIME_HOUR_RATES.overtimeCompensatoryHourMultiplier,
    ),
    latePenaltyBrackets: bracketsToForm(reg.latePenaltyBrackets),
    earlyLeavePenaltyBrackets: bracketsToForm(reg.earlyLeavePenaltyBrackets),
    forgottenCheckIn: punchRuleToForm(reg.forgottenCheckIn),
    forgottenCheckOut: punchRuleToForm(reg.forgottenCheckOut),
    missingBreak: punchRuleToForm(reg.missingBreak),
    specialPenalties: Array.isArray(reg.specialPenalties)
      ? reg.specialPenalties.map((item) => ({
          code: item.code || "",
          labelAr: item.labelAr || "",
          type: item.type || "FINANCIAL_PENALTY",
          days: item.days == null ? "" : String(item.days),
        }))
      : [],
    approvalDelayPenalty: approvalDelayToForm(reg.approvalDelayPenalty),
  };
}

function ActionSelect({ value, disabled, onChange }) {
  return (
    <select
      className="form-select form-select-sm"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {PENALTY_ACTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function BracketEditor({ label, hint, rows, onChange, disabled }) {
  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>{label}</strong>
        {!disabled && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => onChange([...rows, emptyBracket()])}
          >
            إضافة شريحة
          </button>
        )}
      </div>
      {hint ? <p className="text-muted small mb-2">{hint}</p> : null}
      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th>حتى (دقيقة)</th>
              <th>نوع الجزاء</th>
              <th>أيام الخصم</th>
              {!disabled && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${label}-${index}`}>
                <td>
                  <input
                    className="form-control form-control-sm"
                    placeholder={
                      index === rows.length - 1 ? "فارغ = أكثر من ذلك" : "15"
                    }
                    value={row.upToMinutes}
                    disabled={disabled}
                    onChange={(e) => {
                      const next = rows.map((item, i) =>
                        i === index
                          ? { ...item, upToMinutes: e.target.value }
                          : item,
                      );
                      onChange(next);
                    }}
                  />
                </td>
                <td>
                  <ActionSelect
                    value={row.action || "NONE"}
                    disabled={disabled}
                    onChange={(action) => {
                      const next = rows.map((item, i) =>
                        i === index
                          ? {
                              ...item,
                              action,
                              days: action === "DEDUCTION" ? item.days : "",
                            }
                          : item,
                      );
                      onChange(next);
                    }}
                  />
                </td>
                <td>
                  <input
                    className="form-control form-control-sm"
                    placeholder={
                      row.action === "DEDUCTION" ? "0.25" : "بدون خصم"
                    }
                    value={row.action === "DEDUCTION" ? row.days : ""}
                    disabled={disabled || row.action !== "DEDUCTION"}
                    onChange={(e) => {
                      const next = rows.map((item, i) =>
                        i === index ? { ...item, days: e.target.value } : item,
                      );
                      onChange(next);
                    }}
                  />
                </td>
                {!disabled && (
                  <td>
                    {rows.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() =>
                          onChange(rows.filter((_, i) => i !== index))
                        }
                      >
                        حذف
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PunchRuleField({ label, value, disabled, onChange }) {
  return (
    <div className="col-md-4">
      <label className="form-label">{label}</label>
      <ActionSelect
        value={value.action || "NONE"}
        disabled={disabled}
        onChange={(action) =>
          onChange({
            ...value,
            action,
            days: action === "DEDUCTION" ? value.days : "",
          })
        }
      />
      <input
        className="form-control mt-2"
        placeholder={value.action === "DEDUCTION" ? "أيام الخصم" : "بدون خصم"}
        value={value.action === "DEDUCTION" ? value.days : ""}
        disabled={disabled || value.action !== "DEDUCTION"}
        onChange={(e) => onChange({ ...value, days: e.target.value })}
      />
    </div>
  );
}

export default function AttendanceRegulations({ userRole }) {
  const canManage =
    isHrFamilyRole(userRole) || userRole === "gm" || userRole === "admin";
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [newName, setNewName] = useState("");

  const selected = useMemo(
    () => list.find((item) => item.id === selectedId) || null,
    [list, selectedId],
  );

  const load = useCallback(async (preferId) => {
    setLoading(true);
    setError("");
    try {
      const data = await getAttendanceRegulations();
      const rows = Array.isArray(data) ? data : [];
      setList(rows);
      const nextId =
        preferId ||
        rows.find((row) => row.isActive)?.id ||
        rows[0]?.id ||
        "";
      setSelectedId(nextId);
      const current = rows.find((row) => row.id === nextId);
      setForm(current ? regulationToForm(current) : null);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل اللوائح"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectRegulation = (id) => {
    setSelectedId(id);
    const current = list.find((row) => row.id === id);
    setForm(current ? regulationToForm(current) : null);
    setSuccess("");
    setError("");
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!selectedId || !form || !canManage) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await updateAttendanceRegulation(selectedId, {
        nameAr: form.nameAr.trim(),
        descriptionAr: form.descriptionAr.trim() || null,
        gracePeriodMinutes: Number(form.gracePeriodMinutes),
        overtimePaidHourMultiplier: Number(form.overtimePaidHourMultiplier),
        overtimeCompensatoryHourMultiplier: Number(
          form.overtimeCompensatoryHourMultiplier,
        ),
        latePenaltyBrackets: formToBrackets(form.latePenaltyBrackets),
        earlyLeavePenaltyBrackets: formToBrackets(
          form.earlyLeavePenaltyBrackets,
        ),
        forgottenCheckIn: formToPunchRule(form.forgottenCheckIn),
        forgottenCheckOut: formToPunchRule(form.forgottenCheckOut),
        missingBreak: formToPunchRule(form.missingBreak),
        specialPenalties: form.specialPenalties
          .filter((item) => item.labelAr.trim())
          .map((item) => ({
            code: item.code.trim() || item.labelAr.trim(),
            labelAr: item.labelAr.trim(),
            type: item.type,
            days: item.days.trim() ? Number(item.days) : null,
          })),
        approvalDelayPenalty: formToApprovalDelay(form.approvalDelayPenalty),
      });
      setSuccess("تم حفظ اللائحة");
      await load(selectedId);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر حفظ اللائحة"));
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    if (!selectedId || !canManage) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await activateAttendanceRegulation(selectedId);
      setSuccess("تم اعتماد لائحة الشركة");
      await load(selectedId);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر اعتماد اللائحة"));
    } finally {
      setSaving(false);
    }
  };

  const handleClone = async () => {
    if (!canManage) return;
    const name = newName.trim() || `${selected?.nameAr || "لائحة"} — نسخة`;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const created = await createAttendanceRegulation({
        nameAr: name,
        cloneFromId: selectedId || undefined,
      });
      setNewName("");
      setSuccess("تم إنشاء لائحة جديدة");
      await load(created?.id);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إنشاء اللائحة"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-4">جاري تحميل اللوائح...</div>;
  }

  return (
    <div className="p-3 p-md-4" dir="rtl">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h4 className="mb-1">لوائح الحضور والانصراف</h4>
          <p className="text-muted mb-0">
            اختار لائحة الشركة: فترة السماح، التأخير، الانصراف المبكر، حساب
            ساعة الإضافي (صرف مالي أو تعويضي)، نسيان البصمة، البريك، جزاء تأخير
            الرد على الطلب، ولائحة الجزاءات الخاصة. يمكن تسجيل إنذار بدون خصم
            لبعض الحالات بدل الخصم.
          </p>
        </div>
        {selected && !selected.isActive && canManage && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving}
            onClick={handleActivate}
          >
            اعتماد لائحة الشركة
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}

      <div className="row g-3">
        <div className="col-lg-4">
          <div className="card">
            <div className="card-header">اللوائح</div>
            <div className="list-group list-group-flush">
              {list.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`list-group-item list-group-item-action text-end ${
                    item.id === selectedId ? "active" : ""
                  }`}
                  onClick={() => selectRegulation(item.id)}
                >
                  <div className="fw-semibold">{item.nameAr}</div>
                  <small>
                    {item.isActive ? "لائحة الشركة الحالية" : item.key}
                  </small>
                </button>
              ))}
              {!list.length && (
                <div className="list-group-item text-muted">لا توجد لوائح</div>
              )}
            </div>
            {canManage && (
              <div className="card-body">
                <label className="form-label">إضافة لائحة بنسخ الحالية</label>
                <div className="input-group">
                  <input
                    className="form-control"
                    placeholder="اسم اللائحة الجديدة"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={saving || !selectedId}
                    onClick={handleClone}
                  >
                    نسخ
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-8">
          {form && selected ? (
            <form className="card" onSubmit={handleSave}>
              <div className="card-header d-flex justify-content-between align-items-center">
                <strong>{selected.nameAr}</strong>
                {selected.isActive && (
                  <span className="badge text-bg-success">معتمدة</span>
                )}
              </div>
              <div className="card-body">
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">اسم اللائحة</label>
                    <input
                      className="form-control"
                      value={form.nameAr}
                      disabled={!canManage}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, nameAr: e.target.value }))
                      }
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">فترة السماح (دقيقة)</label>
                    <input
                      type="number"
                      min="0"
                      max="180"
                      className="form-control"
                      value={form.gracePeriodMinutes}
                      disabled={!canManage}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          gracePeriodMinutes: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <strong>لائحة الإضافي (أوفر تايم)</strong>
                  <p className="text-muted small mb-2">
                    كل ساعة زيادة تُحسب بالمعدل أدناه حسب نوع التسوية. يظهر
                    للموظف في طلب الإضافي داخل التطبيق.
                  </p>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">
                        صرف مالي — الساعة تُحسب بـ
                      </label>
                      <input
                        type="number"
                        min="0.25"
                        max="5"
                        step="0.25"
                        className="form-control"
                        value={form.overtimePaidHourMultiplier}
                        disabled={!canManage}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            overtimePaidHourMultiplier: e.target.value,
                          }))
                        }
                        required
                      />
                      <div className="form-text">
                        حالياً:{" "}
                        {formatOvertimeHourMultiplierAr(
                          Number(form.overtimePaidHourMultiplier) ||
                            DEFAULT_OVERTIME_HOUR_RATES.overtimePaidHourMultiplier,
                        )}{" "}
                        في المرتب. مثال 1.5 = ساعة ونصف.
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        إذن تعويضي — الساعة تُحسب بـ
                      </label>
                      <input
                        type="number"
                        min="0.25"
                        max="5"
                        step="0.25"
                        className="form-control"
                        value={form.overtimeCompensatoryHourMultiplier}
                        disabled={!canManage}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            overtimeCompensatoryHourMultiplier: e.target.value,
                          }))
                        }
                        required
                      />
                      <div className="form-text">
                        حالياً:{" "}
                        {formatOvertimeHourMultiplierAr(
                          Number(form.overtimeCompensatoryHourMultiplier) ||
                            DEFAULT_OVERTIME_HOUR_RATES.overtimeCompensatoryHourMultiplier,
                        )}{" "}
                        رصيد ساعات. مثال 1 = ساعة بساعة.
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">الوصف</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={form.descriptionAr}
                    disabled={!canManage}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        descriptionAr: e.target.value,
                      }))
                    }
                  />
                </div>

                <BracketEditor
                  label="لائحة التأخير"
                  hint="أول شريحة تطابق الدقائق تُطبَّق. الصف الأخير بدون حد = أكثر من ذلك. لكل شريحة: بدون جزاء، إنذار بدون خصم، أو خصم أيام."
                  rows={form.latePenaltyBrackets}
                  disabled={!canManage}
                  onChange={(rows) =>
                    setForm((prev) => ({ ...prev, latePenaltyBrackets: rows }))
                  }
                />
                <BracketEditor
                  label="لائحة الانصراف المبكر"
                  hint="لكل شريحة يمكن اختيار بدون جزاء، إنذار بدون خصم، أو خصم أيام."
                  rows={form.earlyLeavePenaltyBrackets}
                  disabled={!canManage}
                  onChange={(rows) =>
                    setForm((prev) => ({
                      ...prev,
                      earlyLeavePenaltyBrackets: rows,
                    }))
                  }
                />

                <div className="row g-3 mb-4">
                  <PunchRuleField
                    label="نسيان تسجيل الحضور"
                    value={form.forgottenCheckIn}
                    disabled={!canManage}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, forgottenCheckIn: value }))
                    }
                  />
                  <PunchRuleField
                    label="نسيان تسجيل الانصراف"
                    value={form.forgottenCheckOut}
                    disabled={!canManage}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, forgottenCheckOut: value }))
                    }
                  />
                  <PunchRuleField
                    label="نقص دخول/خروج البريك"
                    value={form.missingBreak}
                    disabled={!canManage}
                    onChange={(value) =>
                      setForm((prev) => ({ ...prev, missingBreak: value }))
                    }
                  />
                </div>

                <div className="mb-4">
                  <strong>جزاء تأخير الرد على طلب</strong>
                  <p className="text-muted small mb-2">
                    يُطبَّق على المسؤول عن الموافقة إذا تأخر في الرد. يمكن تحويله
                    إلى إنذار بدون خصم أو إيقاف الجزاء.
                  </p>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">نوع الجزاء</label>
                      <ActionSelect
                        value={form.approvalDelayPenalty.action}
                        disabled={!canManage}
                        onChange={(action) =>
                          setForm((prev) => ({
                            ...prev,
                            approvalDelayPenalty: {
                              ...prev.approvalDelayPenalty,
                              action,
                              days:
                                action === "DEDUCTION"
                                  ? prev.approvalDelayPenalty.days || "0.25"
                                  : "",
                            },
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">أيام الخصم</label>
                      <input
                        className="form-control"
                        placeholder="0.25"
                        value={
                          form.approvalDelayPenalty.action === "DEDUCTION"
                            ? form.approvalDelayPenalty.days
                            : ""
                        }
                        disabled={
                          !canManage ||
                          form.approvalDelayPenalty.action !== "DEDUCTION"
                        }
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            approvalDelayPenalty: {
                              ...prev.approvalDelayPenalty,
                              days: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">ساعات المهلة</label>
                      <input
                        type="number"
                        min="1"
                        max="720"
                        className="form-control"
                        value={form.approvalDelayPenalty.hours}
                        disabled={!canManage}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            approvalDelayPenalty: {
                              ...prev.approvalDelayPenalty,
                              hours: e.target.value,
                            },
                          }))
                        }
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">ساعات مهلة المدير العام</label>
                      <input
                        type="number"
                        min="1"
                        max="720"
                        className="form-control"
                        value={form.approvalDelayPenalty.gmHours}
                        disabled={!canManage}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            approvalDelayPenalty: {
                              ...prev.approvalDelayPenalty,
                              gmHours: e.target.value,
                            },
                          }))
                        }
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>لائحة الجزاءات الخاصة</strong>
                  {canManage && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          specialPenalties: [
                            ...prev.specialPenalties,
                            {
                              code: "",
                              labelAr: "",
                              type: "FINANCIAL_PENALTY",
                              days: "",
                            },
                          ],
                        }))
                      }
                    >
                      إضافة بند
                    </button>
                  )}
                </div>
                <p className="text-muted small">
                  تظهر في شاشة الجزاءات كأسباب جاهزة (إنذار بدون خصم أو خصم
                  أيام). لا تُطبَّق تلقائياً.
                </p>
                {form.specialPenalties.map((item, index) => (
                  <div className="row g-2 mb-2" key={`sp-${index}`}>
                    <div className="col-md-4">
                      <input
                        className="form-control"
                        placeholder="اسم المخالفة"
                        value={item.labelAr}
                        disabled={!canManage}
                        onChange={(e) => {
                          const next = form.specialPenalties.map((row, i) =>
                            i === index
                              ? { ...row, labelAr: e.target.value }
                              : row,
                          );
                          setForm((prev) => ({ ...prev, specialPenalties: next }));
                        }}
                      />
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={item.type}
                        disabled={!canManage}
                        onChange={(e) => {
                          const next = form.specialPenalties.map((row, i) =>
                            i === index ? { ...row, type: e.target.value } : row,
                          );
                          setForm((prev) => ({ ...prev, specialPenalties: next }));
                        }}
                      >
                        <option value="FINANCIAL_PENALTY">خصم أيام</option>
                        <option value="WARNING">إنذار بدون خصم</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <input
                        className="form-control"
                        placeholder="أيام الخصم"
                        value={item.days}
                        disabled={!canManage || item.type === "WARNING"}
                        onChange={(e) => {
                          const next = form.specialPenalties.map((row, i) =>
                            i === index ? { ...row, days: e.target.value } : row,
                          );
                          setForm((prev) => ({ ...prev, specialPenalties: next }));
                        }}
                      />
                    </div>
                    {canManage && (
                      <div className="col-md-2">
                        <button
                          type="button"
                          className="btn btn-outline-danger w-100"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              specialPenalties: prev.specialPenalties.filter(
                                (_, i) => i !== index,
                              ),
                            }))
                          }
                        >
                          حذف
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {canManage && (
                <div className="card-footer text-start">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    حفظ التعديلات
                  </button>
                </div>
              )}
            </form>
          ) : (
            <div className="alert alert-secondary">اختر لائحة من القائمة</div>
          )}
        </div>
      </div>
    </div>
  );
}
